// Level display: a port of the app's widgets/xp_level.dart. XP is paid by the
// server and celebrated by WebApp's XpCelebrations; these only show it.
import React from 'react';
import { useT } from '../i18n';
import { XP_FOUNDER_TITLE, XP_TITLES } from '../lib/xp';
import { Modal } from './ui';

/** Name of a level-up chest prize (the app's dailySpinPrizeName). */
export function prizeName(t, prize) {
  switch (prize) {
    case 'aiNutrition': return t('dailySpinPrizeAiNutrition');
    case 'aiPhoto': return t('dailySpinPrizeAiPhoto');
    case 'aiTraining': return t('dailySpinPrizeAiTraining');
    case 'freezeProgram': return t('dailySpinPrizeFreezeProgram');
    case 'freezeNutrition': return t('dailySpinPrizeFreezeNutrition');
    case 'adFree7d': return t('dailySpinPrizeAdFree', 7);
    default: return prize;
  }
}

const titleLine = (t, state) => (state.founder
  ? `${t.xpTitle(state.title.id)} · ${t.xpTitle(XP_FOUNDER_TITLE)}`
  : t.xpTitle(state.title.id));

// Outline of a rounded square of side [s], starting at the top centre so the
// fill grows clockwise from there.
function framePath(s, r, inset) {
  const a = inset;
  const b = s - inset;
  const c = s / 2;
  return `M ${c} ${a} L ${b - r} ${a} A ${r} ${r} 0 0 1 ${b} ${a + r} L ${b} ${b - r} A ${r} ${r} 0 0 1 ${b - r} ${b}`
    + ` L ${a + r} ${b} A ${r} ${r} 0 0 1 ${a} ${b - r} L ${a} ${a + r} A ${r} ${r} 0 0 1 ${a + r} ${a} Z`;
}

const FRAME = 50;
const STROKE = 3;

/**
 * The profile avatar ([children]) inside a frame that fills toward the next
 * level, with a small level badge on its corner. `data-xp-target` is where
 * the "+XP" pill flies to.
 */
export function XpFrame({ state, children }) {
  // Concentric with the avatar's 8px corners: 2px gap + half the stroke.
  const d = framePath(FRAME, 8 + 2 + STROKE / 2, STROKE / 2);
  return (
    <span className="gm-xp-frame" data-xp-target>
      <svg width={FRAME} height={FRAME} viewBox={`0 0 ${FRAME} ${FRAME}`} aria-hidden>
        <path d={d} fill="none" stroke="var(--gm-insight)" strokeOpacity="0.22" strokeWidth={STROKE} />
        <path
          d={d}
          fill="none"
          stroke="var(--gm-insight)"
          strokeWidth={STROKE}
          strokeLinecap="round"
          pathLength="100"
          strokeDasharray="100"
          strokeDashoffset={100 * (1 - state.progress)}
          style={{ transition: 'stroke-dashoffset 0.7s cubic-bezier(0.22,1,0.36,1)', opacity: state.progress > 0 ? 1 : 0 }}
        />
      </svg>
      {children}
      <span className="gm-xp-frame__level">{state.level}</span>
    </span>
  );
}

function XpBar({ state }) {
  return (
    <div className="gm-xp-bar">
      <div className="gm-xp-bar__fill" style={{ width: `${state.progress * 100}%` }} />
    </div>
  );
}

/** Top row of the profile menu: level, title and progress. */
export function XpMenuHeader({ state, onOpen }) {
  const t = useT();
  return (
    <button type="button" className="gm-xp-menu" onClick={onOpen}>
      <span className="gm-strong">{`${t('xpLevel', state.level)} · ${titleLine(t, state)}`}</span>
      <XpBar state={state} />
      <span className="gm-small gm-muted">{`${t('xpProgress', state.into, state.cost)} · ${t('xpToNext', state.toNext)}`}</span>
    </button>
  );
}

/** Level card in the desktop sidebar (the top bar with the avatar is phone-only). */
export function XpSidebarCard({ state, onOpen }) {
  const t = useT();
  return (
    <button type="button" className="gm-xp-card" onClick={onOpen} data-xp-target>
      <span className="gm-xp-card__level">{state.level}</span>
      <span className="gm-xp-card__body">
        <span className="gm-strong">{titleLine(t, state)}</span>
        <XpBar state={state} />
        <span className="gm-small gm-muted">{t('xpToNext', state.toNext)}</span>
      </span>
    </button>
  );
}

/** Every title with the level it unlocks at, and how XP is earned. */
export function XpTitlesModal({ open, onClose, state }) {
  const t = useT();
  const row = (name, subtitle, unlocked) => (
    <div key={name} className={`gm-xp-title${unlocked ? '' : ' gm-xp-title--locked'}`}>
      <span className="gm-xp-title__icon">{unlocked ? '👑' : '🔒'}</span>
      <span>
        <span className="gm-strong">{name}</span>
        <span className="gm-small gm-muted" style={{ display: 'block' }}>{subtitle}</span>
      </span>
    </div>
  );
  return (
    <Modal open={open} onClose={onClose} title={`${t('xpLevel', state.level)} · ${t('xpProgress', state.into, state.cost)}`}>
      <div className="gm-strong" style={{ marginBottom: 6 }}>{t('xpTitlesTitle')}</div>
      {state.founder && row(t.xpTitle(XP_FOUNDER_TITLE), t('xpFounderNote'), true)}
      {XP_TITLES.map((title) => row(t.xpTitle(title.id), t('xpTitleUnlocksAt', title.level), state.level >= title.level))}
      <div className="gm-xp-howto">
        <div className="gm-strong" style={{ marginBottom: 6 }}>{t('xpHowToEarn')}</div>
        <div style={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>{t('xpHowToEarnBody')}</div>
      </div>
    </Modal>
  );
}
